import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { RideWiseRow, VehicleRow, DriverRow } from "./types";
import { fmtNum, safeArr } from "./helpers";

interface Props {
  rw: RideWiseRow[];
  allRw?: RideWiseRow[];
  selectedYears?: string[];
  selectedMonths?: string[];
  vehiclesById: Map<string, VehicleRow>;
  driversById: Map<string, DriverRow>;
  loading: boolean;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const YEAR_COLOURS = [
  "#a855f7", // purple (current accent for hoist)
  "#3b82f6", // blue
  "#22c55e", // green
  "#eab308", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
  "#f97316", // orange
  "#ec4899", // pink
];

const HoistDemandTab: React.FC<Props> = ({
  rw,
  allRw,
  selectedYears,
  selectedMonths,
  vehiclesById,
  driversById,
  loading,
}) => {
  const data = useMemo(() => {
    const list = safeArr(rw);
    const total = list.length;
    const hoist = list.filter((r) => r.hoist_deployed).length;

    const byVehicle = new Map<string, number>();
    const byDriver = new Map<string, number>();
    const byDay = Array.from({ length: 7 }, () => 0);
    const byHour = Array.from({ length: 24 }, () => 0);
    const tmvVehicles = new Set<string>();

    for (const r of list) {
      if ((r.vehicle_identifier || "").trim()) {
        const v = vehiclesById.get(String(r.vehicle_identifier));
        if (v && (v.vehicle_type || "").toLowerCase().includes("hoist")) {
          tmvVehicles.add(String(r.vehicle_identifier));
        }
      }
      if (!r.hoist_deployed) continue;
      const veh = String(r.vehicle_identifier || "—");
      byVehicle.set(veh, (byVehicle.get(veh) || 0) + 1);
      const drv = String(r.driver_number || "—");
      const drow = driversById.get(drv);
      const drvLabel = drow?.driver_name || `#${drv}`;
      byDriver.set(drvLabel, (byDriver.get(drvLabel) || 0) + 1);
      if (r.trip_date) {
        const dt = new Date(r.trip_date);
        if (!Number.isNaN(dt.getTime())) byDay[dt.getDay()] += 1;
      }
      if (r.start_time) {
        const hr = Number(String(r.start_time).slice(0, 2));
        if (Number.isFinite(hr) && hr >= 0 && hr < 24) byHour[hr] += 1;
      }
    }

    return {
      total, hoist,
      hoistPct: total ? (hoist / total) * 100 : 0,
      byVehicle: Array.from(byVehicle.entries()).map(([vehicle, hoist]) => ({ vehicle, hoist })).sort((a, b) => b.hoist - a.hoist).slice(0, 20),
      byDriver: Array.from(byDriver.entries()).map(([driver, hoist]) => ({ driver, hoist })).sort((a, b) => b.hoist - a.hoist).slice(0, 20),
      byDay: byDay.map((v, i) => ({ day: DAYS[i], hoist: v })),
      byHour: byHour.map((v, i) => ({ hour: `${String(i).padStart(2, "0")}:00`, hoist: v })),
      pressure: tmvVehicles.size ? hoist / tmvVehicles.size : 0,
      tmvCount: tmvVehicles.size,
    };
  }, [rw, vehiclesById, driversById]);

  // Per-year monthly hoist chart
  const { byMonthYearData, yearsToPlot } = useMemo(() => {
    const source = safeArr(allRw && allRw.length ? allRw : rw);

    const monthsAllowed =
      !selectedMonths ||
      selectedMonths.includes("all") ||
      selectedMonths.length === 0
        ? null
        : new Set(selectedMonths);

    const yearMap = new Map<string, number[]>();

    for (const r of source) {
      if (!r.hoist_deployed) continue;
      const date =
        (r as any).trip_date ||
        (r as any).Trip_Date ||
        (r as any).pu_date ||
        (r as any).created_at ||
        "";
      const iso = String(date).slice(0, 10);
      if (iso.length < 7) continue;
      const year = iso.slice(0, 4);
      const monthStr = iso.slice(5, 7);
      if (monthsAllowed && !monthsAllowed.has(monthStr)) continue;
      const monthIdx = Number(monthStr) - 1;
      if (monthIdx < 0 || monthIdx > 11) continue;

      let arr = yearMap.get(year);
      if (!arr) {
        arr = new Array(12).fill(0);
        yearMap.set(year, arr);
      }
      arr[monthIdx] += 1;
    }

    let years: string[];
    if (
      selectedYears &&
      !selectedYears.includes("all") &&
      selectedYears.length > 0
    ) {
      const requested = new Set(selectedYears);
      const minYear = Math.min(...selectedYears.map((y) => Number(y)));
      if (Number.isFinite(minYear)) {
        requested.add(String(minYear - 1));
      }
      years = Array.from(requested)
        .filter((y) => yearMap.has(y))
        .sort();
    } else {
      years = Array.from(yearMap.keys()).sort();
    }

    const data = MONTH_LABELS.map((label, i) => {
      const row: Record<string, number | string> = { month: label };
      for (const y of years) {
        const arr = yearMap.get(y);
        row[y] = arr ? arr[i] : 0;
      }
      return row;
    });

    return { byMonthYearData: data, yearsToPlot: years };
  }, [allRw, rw, selectedYears, selectedMonths]);

  if (loading) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">Loading hoist demand…</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-card border border-border">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Total Hoist Trips</div>
            <div className="text-2xl font-bold">{fmtNum(data.hoist)}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Hoist %</div>
            <div className="text-2xl font-bold">{data.hoistPct.toFixed(1)}%</div>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Hoist Pressure Score</div>
            <div className="text-2xl font-bold">{data.pressure.toFixed(1)}</div>
            <div className="text-xs text-muted-foreground">trips per active TMV vehicle ({data.tmvCount})</div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border border-border">
        <CardHeader>
          <CardTitle className="text-base">Hoist by Month — by Year</CardTitle>
        </CardHeader>
        <CardContent>
          {yearsToPlot.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No hoist data to chart for this selection.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={byMonthYearData}>
                <CartesianGrid stroke="#444" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#aaa" />
                <YAxis stroke="#aaa" />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {yearsToPlot.map((year, idx) => (
                  <Line
                    key={year}
                    type="monotone"
                    dataKey={year}
                    name={year}
                    stroke={YEAR_COLOURS[idx % YEAR_COLOURS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="bg-card border border-border">
          <CardHeader><CardTitle className="text-base">Hoist by Vehicle (Top 20)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.byVehicle}>
                <CartesianGrid stroke="#444" strokeDasharray="3 3" />
                <XAxis dataKey="vehicle" stroke="#aaa" angle={-30} textAnchor="end" height={60} />
                <YAxis stroke="#aaa" />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                <Bar dataKey="hoist" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border">
          <CardHeader><CardTitle className="text-base">Hoist by Driver (Top 20)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.byDriver}>
                <CartesianGrid stroke="#444" strokeDasharray="3 3" />
                <XAxis dataKey="driver" stroke="#aaa" angle={-30} textAnchor="end" height={80} interval={0} />
                <YAxis stroke="#aaa" />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                <Bar dataKey="hoist" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="bg-card border border-border">
          <CardHeader><CardTitle className="text-base">Demand by Day of Week</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.byDay}>
                <CartesianGrid stroke="#444" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#aaa" />
                <YAxis stroke="#aaa" />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                <Bar dataKey="hoist" fill="#eab308" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border">
          <CardHeader><CardTitle className="text-base">Demand by Hour</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.byHour}>
                <CartesianGrid stroke="#444" strokeDasharray="3 3" />
                <XAxis dataKey="hour" stroke="#aaa" angle={-30} textAnchor="end" height={50} />
                <YAxis stroke="#aaa" />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                <Bar dataKey="hoist" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HoistDemandTab;
